/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { TotalBeneficiariesComponent } from "./TotalBeneficiariesComponent";
import React, { useState, useEffect } from "react";
import {
	Container,
	Grid,
	Box,
	Typography,
	Stack,
	CircularProgress,
} from "@mui/material";
import Header from "./Header";
import { EventsAndAlertsComponent } from "./EventsAndAlertsComponent";
import { useUserStore } from "../../stores/user.store";
import { useCreateAuditlog } from "../../hooks/useAuditlog";
import BasicKPIComponent from "./BasicKPIComponent";
import useKPIData from "../../hooks/useKPIData";
import { useTranslation } from "react-i18next";
import { useCurrencySetting, useSettings } from "../../hooks/useSetting";
import { formatCommaSeparatedNumber } from "../../utils/general";
import AverageComponent from "./AverageComponent";
import {
	useGetProgramsByIds,
	useGetProvidersByProgramId,
} from "../../hooks/useBeneficiaries";
import MultiTreatmentDashboard from "../../components/MultiTreatmentDashboard";
const { QlikEmbed } = require("@qlik/embed-react"); // eslint-disable-line @typescript-eslint/no-var-requires

interface Filters {
	programId: string;
	providerId: string;
	providerSiteId: string;
	search: string;
	pageNumber: number;
	pageSize: number;
	dateRange: "lastMonth" | "lastQuarter" | "lastYear";
	zipCode: string;
}

const APP_ID = process.env.REACT_APP_QLIK_APP_ID || "";

const Dashboard: React.FC = () => {
	const { userInfo, userHasAccessToCode } = useUserStore();
	const { data: settings, isLoading } = useSettings(userInfo?.id);
	const { t } = useTranslation("dashboard");
	const currencySetting = useCurrencySetting();
	const createAuditLog = useCreateAuditlog();

	const [qlikApi, setQlikApi] = useState<any>(null);
	const [filters, setFilters] = useState<Filters>({
		programId: "",
		providerId: "",
		providerSiteId: "",
		search: "",
		pageNumber: 0,
		pageSize: 10,
		dateRange: "lastMonth",
		zipCode: "",
	});

	const { data: kpiData, refetch } = useKPIData(userInfo?.id || "");

	const { data: providers } = useGetProvidersByProgramId(
		filters.programId ? [filters.programId] : [],
		filters.providerSiteId
			? filters.providerSiteId.split(",").filter(Boolean)
			: [],
		true,
	);

	// Get user's programs to check for multi-treatment programs
	const programIds =
		userInfo?.programs?.map((program) => program.programId) || [];
	const { data: programs } = useGetProgramsByIds(programIds);

	// Check if user has any multi-treatment programs
	const hasMultiTreatmentProgram = programs?.some(
		(program) => program.multiTreatmentPlan === true,
	);

	useEffect(() => {
		if (userInfo?.id) {
			refetch();
		}
	}, [userInfo?.id, filters, refetch]);

	useEffect(() => {
		createAuditLog.mutateAsync({
			appType: "WEB_BACK_OFFICE",
			module: "Dashboard",
			action: "When entering",
			detail: "Access to dashboard",
			actionCode: "WEB_DASH_ACCESS",
			option: "Dashboard",
			createdBy: userInfo?.id as string,
		});
	}, []);

	const applyFilters = () => {
		if (!filters.programId) {
			return;
		}

		if (!qlikApi) {
			return;
		}

		const now = new Date();
		const start = new Date(now);
		const daysBack =
			filters.dateRange === "lastYear"
				? 365
				: filters.dateRange === "lastQuarter"
					? 90
					: 30;
		start.setDate(now.getDate() - daysBack);

		const startDate = start.toISOString().slice(0, 10);
		const today = now.toISOString().slice(0, 10);

		qlikApi
			.getDoc()
			.then(({ app }: any) => {
				app.clearAll();
				app
					.getVariableByName("vStartDate")
					.then((v: any) => v.setStringValue(startDate));
				app
					.getVariableByName("vEndDate")
					.then((v: any) => v.setStringValue(today));

				const select = (fieldName: string, values: string | string[]) => {
					const qValues = Array.isArray(values)
						? values.map((v) => ({ qText: v }))
						: [{ qText: values }];
					return app
						.getField(fieldName)
						.then((f: any) => f.selectValues(qValues, false, false))
						.catch((error: any) => {
							console.log(
								`Field ${fieldName} not found, skipping selection:`,
								error,
							);
							return Promise.resolve();
						});
				};

				const promises: any = [];

				if (filters.programId) {
					app
						.getVariableByName("vSelectedProgram")
						.then((v: any) => v.setStringValue(filters.programId));
				}

				app
					.getVariableByName("vSelectedOU")
					.then((v: any) =>
						v.setStringValue(filters.providerSiteId.replaceAll(",", "|")),
					);

				app
					.getVariableByName("vSelectedProvider")
					.then((v: any) =>
						v.setStringValue(
							filters.providerId === "all" || !filters.providerId
								? providers?.map((p) => p.key).join("|")
								: filters.providerId,
						),
					);

				if (filters.zipCode) {
					promises.push(
						select("organizations.address_zipCode", filters.zipCode),
					);
				}

				// Set beneficiary term setting variables for QLIK
				if (userInfo?.accountId) {
					app
						.getVariableByName("vAccountId_settings")
						.then((v: any) => v.setStringValue(userInfo.accountId))
						.catch((e: Error) => console.log(e));
				}
				if (beneficiaryTermSettingId) {
					app
						.getVariableByName("vSettingId")
						.then((v: any) => v.setStringValue(beneficiaryTermSettingId))
						.catch((e: Error) => console.log(e));
				}

				return Promise.all(promises);
			})
			.catch();
	};

	const actionsAndCards = settings?.qlikCardsConfig?.actionsAndCards || [];
	const beneficiaryTermSettingId =
		settings?.qlikCardsConfig?.beneficiaryTermSettingId || "";

	if (isLoading || !userInfo?.id) return <></>;

	const LoaderContainer = ({ height, children }: any) => {
		return (
			<Box
				sx={{
					position: "relative",
					width: "100%",
					height,
					bgcolor: "background.paper",
				}}
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						zIndex: 1,
						pointerEvents: "none",
					}}
				>
					<CircularProgress />
				</Box>
				<Box sx={{ position: "relative", zIndex: 2, height: "100%" }}>
					{children}
				</Box>
			</Box>
		);
	};

	// If user has multi-treatment programs, show the MultiTreatmentDashboard
	if (hasMultiTreatmentProgram && settings?.useQlikDashboard) {
		return (
			<MultiTreatmentDashboard
				filters={filters}
				setFilters={setFilters}
				applyFilters={applyFilters}
				actionsAndCards={actionsAndCards}
				qlikApi={qlikApi}
				setQlikApi={setQlikApi}
				beneficiaryTermSettingId={beneficiaryTermSettingId}
			/>
		);
	}

	return settings?.useQlikDashboard ? (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			{userHasAccessToCode("DASH_VIEW") && (
				<>
					<Header
						filtersState={[filters, setFilters]}
						handleSearch={applyFilters}
					/>

					{actionsAndCards.map((section, sectionIdx) => {
						if (!userHasAccessToCode(section.action)) return null;

						if (sectionIdx === 1) {
							const cards = section.cards.filter((c) =>
								userHasAccessToCode(c.action),
							);
							return (
								<Grid container spacing={2} sx={{ mb: 4 }} key={sectionIdx}>
									<Grid item xs={12} md={7}>
										<Box
											sx={{
												height: 364,
												bgcolor: "background.paper",
												borderRadius: 2,
												boxShadow: 1,
												overflowY: "auto",
											}}
										>
											<EventsAndAlertsComponent
												gridProps={{
													sx: {
														bgcolor: "white",
														minWidth: 200,
														minHeight: 364,
														height: 364,
													},
												}}
											/>
										</Box>
									</Grid>
									<Grid
										item
										xs={12}
										md={2.5}
										container
										spacing={1.5}
										direction="column"
									>
										{cards.slice(1, 3).map((card, idx) => (
											<Grid item key={card.cardId}>
												{LoaderContainer({
													height: 176,
													children: (
														<Box sx={{ height: 176 }}>
															<QlikEmbed
																exposeRefApi={
																	idx === 0 ? setQlikApi : undefined
																}
																ui="analytics/chart"
																appId={APP_ID}
																objectId={card.cardId}
																identity={userInfo?.id}
																style={{ width: "100%", height: "100%" }}
															/>
														</Box>
													),
												})}
											</Grid>
										))}
									</Grid>
									<Grid
										item
										xs={12}
										md={2.5}
										container
										spacing={2}
										direction="column"
									>
										{cards.slice(3).map((card) => (
											<Grid item key={card.cardId}>
												{LoaderContainer({
													height: 364,
													children: (
														<Box sx={{ height: 364 }}>
															<QlikEmbed
																ui="analytics/chart"
																appId={APP_ID}
																objectId={card.cardId}
																identity={userInfo?.id}
																style={{ width: "100%", height: "100%" }}
															/>
														</Box>
													),
												})}
											</Grid>
										))}
									</Grid>
								</Grid>
							);
						}

						if (sectionIdx === 2 || sectionIdx === 3) {
							const cards = section.cards.filter((c) =>
								userHasAccessToCode(c.action),
							);
							return (
								<React.Fragment key={sectionIdx}>
									{section.title && (
										<Typography variant="headlineSmall">
											{section.title}
										</Typography>
									)}
									<Grid container spacing={2} sx={{ mb: 4 }} key={sectionIdx}>
										<Grid item xs={12} md={5}>
											{LoaderContainer({
												height: 224,
												children: (
													<Box sx={{ height: 224 }}>
														<QlikEmbed
															ui="analytics/chart"
															appId={APP_ID}
															objectId={cards[0].cardId}
															identity={userInfo?.id}
															style={{ width: "100%", height: "100%" }}
														/>
													</Box>
												),
											})}
										</Grid>
										<Grid item xs={12} md={7} container spacing={2}>
											{cards.slice(1).map((card) => (
												<Grid item xs={12} md={6} key={card.cardId}>
													{LoaderContainer({
														height: 224,
														children: (
															<Box
																sx={{
																	height: 224,
																}}
															>
																<QlikEmbed
																	ui="analytics/chart"
																	appId={APP_ID}
																	objectId={card.cardId}
																	identity={userInfo?.id}
																	style={{ width: "100%", height: "100%" }}
																/>
															</Box>
														),
													})}
												</Grid>
											))}
										</Grid>
									</Grid>
								</React.Fragment>
							);
						}

						return (
							<React.Fragment key={sectionIdx}>
								{section.title && (
									<Typography variant="headlineSmall">
										{section.title}
									</Typography>
								)}
								<Grid container spacing={2} sx={{ mb: 4 }}>
									{section.cards.map((card) =>
										card.cardId && userHasAccessToCode(card.action) ? (
											<Grid item key={card.cardId} xs={12} sm={6} md={3}>
												{LoaderContainer({
													height: 196,
													children: (
														<Box sx={{ height: 196 }}>
															<QlikEmbed
																ui="analytics/chart"
																appId={APP_ID}
																objectId={card.cardId}
																identity={userInfo?.id}
																style={{ width: "100%", height: "100%" }}
															/>
														</Box>
													),
												})}
											</Grid>
										) : null,
									)}
								</Grid>
							</React.Fragment>
						);
					})}
					{/* <Grid container spacing={2} sx={{ mb: 4 }} key={"qwewqe"}>
						<Grid item xs={12} md={7}>
							<Box
								sx={{
									backgroundColor: "red",
									color: "white",
									padding: 2,
									textAlign: "center",
									fontSize: "24px",
									minHeight: "100px",
								}}
							>
								SOMETHING - THIS SHOULD BE VISIBLE
							</Box>
						</Grid>
					</Grid> */}
				</>
			)}
		</Container>
	) : (
		<Container
			disableGutters={true}
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 120px)",
			}}
		>
			<Header filtersState={[filters, setFilters]} />

			{userHasAccessToCode("DASH_VIEW") && (
				<Box
					sx={{
						flex: "1 1 auto",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Grid
						container
						rowSpacing={2}
						sx={{ flex: "0 0 auto", display: { xs: "none", md: "flex" } }}
					>
						<Grid item container spacing={2} marginBottom={2}>
							<BasicKPIComponent
								title={t("totalUDTs")}
								content={kpiData?.totalUDTs || 0}
								footer={{
									hidden: true,
									increased: true,
									label: "11%",
									description: t("past30Days"),
								}}
							/>
							<BasicKPIComponent
								title={t("totalIncentivesEarned")}
								content={
									currencySetting.mask +
									formatCommaSeparatedNumber(
										Math.abs(kpiData?.totalIncentivesEarned || 0),
									)
								}
								footer={{
									hidden: true,
									increased: true,
									label: "",
									description: "",
								}}
							/>
							<BasicKPIComponent
								title={t("totalIncentivesReceived")}
								content={
									currencySetting.mask +
									formatCommaSeparatedNumber(
										Math.abs(kpiData?.totalIncentivesReceived || 0),
									)
								}
								footer={{
									hidden: true,
									increased: true,
									label: "5%",
									description: t("past30Days"),
								}}
							/>
							<BasicKPIComponent
								title={t("currentIncentivesBanked")}
								content={
									currencySetting.mask +
									formatCommaSeparatedNumber(
										Math.abs(kpiData?.currentIncentivesBanked || 0),
									)
								}
								footer={{
									hidden: true,
									increased: true,
									label: "5%",
									description: t("past30Days"),
								}}
							/>
						</Grid>
					</Grid>
					<Box
						sx={{
							flex: "1 1 0px",
							overflowY: "auto",
						}}
					>
						<EventsAndAlertsComponent />
					</Box>
					{false && (
						<>
							<Grid item container direction={"column"} rowSpacing={2} md={2.5}>
								<AverageComponent
									title={t("avgLengthOfTreatment")}
									icon="lightBulb"
									content={
										<Typography align="left" variant="headlineSmall">
											{t("weeks", { count: 11 })}
										</Typography>
									}
									increased
									footerLabel="12 %"
									footerDescription={t("past30Days")}
									boxProps={{ xs: 12, sm: 12, md: 2, item: true }}
								/>
								<AverageComponent
									title={t("avgLengthOfTreatment")}
									icon="lightBulb"
									content={
										<Stack direction={"row"} alignItems={"end"}>
											<Typography align="left" variant="headlineSmall">
												75%
											</Typography>
											<Typography
												align="left"
												color={(theme) => theme.palette.textMain.main2}
												variant="bodyLarge"
											>
												({t("bankedEarned")})
											</Typography>
										</Stack>
									}
									increased={false}
									footerLabel="12 %"
									footerDescription={t("past30Days")}
									boxProps={{ xs: 12, sm: 12, md: 2, item: true }}
								/>
							</Grid>
							<TotalBeneficiariesComponent />
						</>
					)}
				</Box>
			)}
		</Container>
	);
};

export default Dashboard;
